import {useSafeAsyncCallback} from '@github-ui/use-safe-async-callback'

import type {UpdateColumnValueAction} from '../api/columns/contracts/domain'
import {MemexColumnDataType} from '../api/columns/contracts/memex-column'
import useToasts from '../components/toasts/use-toasts'
import {getEnabledFeatures} from '../helpers/feature-flags'
import type {MemexItemModel} from '../models/memex-item-model'
import {ApiError} from '../platform/api-error'
import {
  MAX_BULK_ITEMS,
  useBulkUpdateItemColumnValues,
} from '../state-providers/column-values/use-bulk-update-item-column-value'
import {useHistory} from '../state-providers/history/history'
import {HistoryResources} from '../strings'
import {UpdateValidationError, validateUpdate} from './use-update-item'

export const useBulkUpdateItems = () => {
  const {bulkUpdateColumnValues} = useBulkUpdateItemColumnValues()

  const {addToast, removeToast} = useToasts()
  const safeRemoveToast = useSafeAsyncCallback(removeToast)

  const history = useHistory()

  const safeHistoryUndo = useSafeAsyncCallback(() => history?.lastAction?.revert('bulk_action_toast'))

  /**
   * @param sourceRepoId If provided, will force labels, assignees, & milestones to be applied within the same repository. This is
   * stricter than the default behavior, which will attempt to search across repositories for matching values.
   * @param description Optional description of the action, used for the history stack. Should be a simple verb phrase.
   */
  const bulkUpdateSingleColumnValue = async (
    items: Array<MemexItemModel>,
    update: UpdateColumnValueAction,
    description = items.length === 1 ? HistoryResources.updateItem : HistoryResources.updateItems,
    sourceRepoId?: number | null,
  ) => {
    if (items.length > MAX_BULK_ITEMS) {
      addToast({
        message: `Error updating items: Cannot update more than ${MAX_BULK_ITEMS} items at once`,
        type: 'error',
      })
      return
    }

    try {
      if (sourceRepoId !== undefined) validateUpdate(items, update, sourceRepoId)
      const itemUpdates = items.map(item => ({itemId: item.id, updates: [update]}))

      const undo = await bulkUpdateColumnValues(itemUpdates)
      const {memex_bulk_actions_background_job} = getEnabledFeatures()
      // Cannot revert repository changes since you can't go from issue back to draft.
      if (
        items.length > 1 &&
        update.dataType !== MemexColumnDataType.Repository &&
        !memex_bulk_actions_background_job
      ) {
        const toastId = addToast({
          message: `${items.length} items updated`,
          type: 'success',
          action: {
            text: 'Undo',
            // use the history api instead of calling directly so that it gets removed from the stack
            // if we just put history.lastAction.undo here, it wouldn't be updated yet
            // --
            // fall back to undo for when the history context is not available, ie flag is disabled
            handleClick: history ? safeHistoryUndo : undo,
          },
        })
        history?.registerAction({
          revert: () => {
            undo()
            safeRemoveToast(toastId)
          },
          description,
        })
      } else {
        history?.registerAction({
          revert: undo,
          description,
        })
      }
    } catch (error) {
      if (error instanceof ApiError && !error.message.includes('not valid JSON')) {
        addToast({
          message: `Error updating items: ${error.message}`,
          type: 'error',
        })
        throw error // rethrow to handle further up and log for stats
      } else if (error instanceof ApiError && error.status && error.status >= 500) {
        addToast({
          message: 'Something went wrong, please try again.',
          type: 'error',
        })
      } else if (error instanceof UpdateValidationError) {
        addToast({
          message: error.message,
          type: 'warning',
        })
      } else {
        throw error
      }
    }
  }

  const bulkUpdateMultipleColumnValues = async (
    itemUpdates: Array<{itemId: number; updates: Array<UpdateColumnValueAction>}>,
    description = itemUpdates.length === 1 ? HistoryResources.updateItem : HistoryResources.updateItems,
  ) => {
    if (itemUpdates.length > MAX_BULK_ITEMS) {
      addToast({
        message: `Error updating items: Cannot update more than ${MAX_BULK_ITEMS} items at once`,
        type: 'error',
      })
      return
    }

    try {
      const undo = await bulkUpdateColumnValues(itemUpdates)
      const {memex_bulk_actions_background_job} = getEnabledFeatures()
      // Cannot revert repository changes since you can't go from issue back to draft.
      if (itemUpdates.length > 1 && !memex_bulk_actions_background_job) {
        const toastId = addToast({
          message: `${itemUpdates.length} items updated`,
          type: 'success',
          action: {
            text: 'Undo',
            // use the history api instead of calling directly so that it gets removed from the stack
            // if we just put history.lastAction.undo here, it wouldn't be updated yet
            // --
            // fall back to undo for when the history context is not available, ie flag is disabled
            handleClick: history ? safeHistoryUndo : undo,
          },
        })
        history?.registerAction({
          revert: () => {
            undo()
            safeRemoveToast(toastId)
          },
          description,
        })
      } else {
        history?.registerAction({
          revert: undo,
          description,
        })
      }
    } catch (error) {
      if (error instanceof ApiError && !error.message.includes('not valid JSON')) {
        addToast({
          message: `Error updating items: ${error.message}`,
          type: 'error',
        })
        throw error // rethrow to handle further up and log for stats
      } else if (error instanceof ApiError && error.status && error.status >= 500) {
        addToast({
          message: 'Something went wrong, please try again.',
          type: 'error',
        })
      } else if (error instanceof UpdateValidationError) {
        addToast({
          message: error.message,
          type: 'warning',
        })
      } else {
        throw error
      }
    }
  }

  return {
    bulkUpdateSingleColumnValue,
    bulkUpdateMultipleColumnValues,
  }
}
